import {
  AUDIT_ACTION_TAGS,
  AUDIT_ACTION_TAG_DEFAULT,
  AUDIT_KIND_SIGN_IN,
  AUDIT_RANGE_CUSTOM,
  AUDIT_RANGE_DAYS,
  AUDIT_RANGE_LAST_30_DAYS,
  AUDIT_RETENTION_DAYS,
} from "../constants/AuditLogConstants";

// The browser's IANA time zone ("Asia/Kolkata"), sent with an export so its
// times are local; undefined when the browser cannot say, and the export is
// then in UTC.
export const browserTimeZone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || undefined;
  } catch {
    return undefined;
  }
};

const partsOf = (date, timeZone) => {
  const parts = {};
  new Intl.DateTimeFormat("en-US", {
    timeZone,
    hourCycle: "h23",
    year: "numeric",
    month: "numeric",
    day: "numeric",
    hour: "numeric",
    minute: "numeric",
    second: "numeric",
  })
    .formatToParts(date)
    .forEach((part) => {
      if (part.type !== "literal") parts[part.type] = Number(part.value);
    });
  return parts;
};

// How far the zone's clock is ahead of UTC at that instant, in milliseconds.
const offsetAt = (time, timeZone) => {
  const whole = Math.floor(time / 1000) * 1000;
  const p = partsOf(new Date(whole), timeZone);
  return Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second) - whole;
};

// The calendar day of an instant in the zone.
const dayOf = (date, timeZone) => {
  const p = partsOf(date, timeZone);
  return { year: p.year, month: p.month - 1, day: p.day };
};

// The instant a calendar day starts in the zone. The offset is looked up
// twice so a day whose start follows a daylight saving change is right.
const startOfDay = ({ year, month, day }, timeZone) => {
  const wall = Date.UTC(year, month, day);
  const guess = wall - offsetAt(wall, timeZone);
  return new Date(wall - offsetAt(guess, timeZone));
};

const addDays = (day, days) => {
  const next = new Date(Date.UTC(day.year, day.month, day.day + days));
  return { year: next.getUTCFullYear(), month: next.getUTCMonth(), day: next.getUTCDate() };
};

// The first day the date picker offers: today and the 364 days before it, in
// the given time zone (the browser's by default). Always later than the API's
// clamp (now - 365 days).
export const earliestDay = (now = new Date(), timeZone = browserTimeZone()) =>
  startOfDay(addDays(dayOf(now, timeZone), -(AUDIT_RETENTION_DAYS - 1)), timeZone);

// The from/to (UTC ISO strings) of a range choice, in the given time zone (the
// browser's by default). A preset is today and the days before it, from local
// midnight ("Last 30 days" starts 29 days before today), and sends no "to": the
// API ends it at its own now, so a browser clock running slow hides no entry. A
// custom range runs from the start of its first day to the end of its last,
// never before earliestDay.
export const dateRange = (range, customRange, now = new Date(), timeZone = browserTimeZone()) => {
  if (range === AUDIT_RANGE_CUSTOM && customRange?.[0]) {
    const first = startOfDay(dayOf(new Date(customRange[0]), timeZone), timeZone);
    const last = dayOf(new Date(customRange[1] || customRange[0]), timeZone);
    const earliest = earliestDay(now, timeZone);
    return {
      from: (first < earliest ? earliest : first).toISOString(),
      to: startOfDay(addDays(last, 1), timeZone).toISOString(),
    };
  }
  const days = AUDIT_RANGE_DAYS[range] || AUDIT_RANGE_DAYS[AUDIT_RANGE_LAST_30_DAYS];
  return {
    from: startOfDay(addDays(dayOf(now, timeZone), -(days - 1)), timeZone).toISOString(),
  };
};

// The list's (and the export's) query parameters; empty filters are undefined
// and left out by the service.
export const listParams = (query, now = new Date(), timeZone = browserTimeZone()) => {
  const { from, to } = dateRange(query.range, query.customRange, now, timeZone);
  return {
    from,
    to,
    kind: query.kind ?? undefined,
    area: query.area || undefined,
    action: query.action || undefined,
    userId: query.userId || undefined,
    search: query.search || undefined,
    pageNo: query.pageNo,
    pageSize: query.pageSize,
    sortBy: query.sortBy,
  };
};

export const actionTag = (action) =>
  AUDIT_ACTION_TAGS[action] || AUDIT_ACTION_TAG_DEFAULT;

export const initials = (name) =>
  (name || "")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0].toUpperCase())
    .join("");

// "India Standard Time": the browser's own name for the time zone (the
// browser's by default) at that date.
export const timeZoneName = (date, timeZone = browserTimeZone()) => {
  try {
    return (
      new Intl.DateTimeFormat("en-US", { timeZone, timeZoneName: "long" })
        .formatToParts(date)
        .find((part) => part.type === "timeZoneName")?.value || ""
    );
  } catch {
    return "";
  }
};

// The details panel's one plain sentence. A sign-in row's record name is its
// method and its summary what went wrong; a sign-in by nobody is an account
// that does not exist (the API's "Unknown account", no id). A role's
// permission change is recognised by its record type and the API's
// "Permission" field header, not by the wording of the summary.
export const auditSentence = (entry, formatMessage) => {
  const who = entry?.actor?.isSystem
    ? formatMessage(
        { id: "audit_the_source" },
        { source: entry.actor.source || formatMessage({ id: "audit_system" }) }
      )
    : entry?.actor?.name;
  const action = (entry?.action || "").toLowerCase();
  const record = entry?.recordName;

  if (entry?.action === "Sign-in failed") {
    const unknown = !entry?.actor?.id && !entry?.actor?.isSystem;
    if (!entry?.summary) {
      return formatMessage(
        { id: unknown ? "audit_sentence_sign_in_unknown_no_reason" : "audit_sentence_sign_in_failed_no_reason" },
        { who }
      );
    }
    return formatMessage(
      { id: unknown ? "audit_sentence_sign_in_unknown" : "audit_sentence_sign_in_failed" },
      { who, reason: entry.summary }
    );
  }
  if (entry?.action === "Password reset") {
    return formatMessage({ id: "audit_sentence_password_reset" }, { who });
  }
  if (entry?.recordType === "Role" && entry?.fieldHeader === "Permission") {
    return formatMessage({ id: "audit_sentence_role_permissions" }, { who, record });
  }
  if (!record || entry?.kind === AUDIT_KIND_SIGN_IN) {
    return formatMessage({ id: "audit_sentence_no_record" }, { who, action });
  }
  return formatMessage({ id: "audit_sentence" }, { who, action, record });
};

// How long entries are kept, in words ("1 year", "2 years", "400 days"): whole
// years when the days are a multiple of 365. The days come from the API
// (auditLogRetentionDays), the standard retention until it has answered.
export const retentionPeriod = (days, formatMessage) => {
  const kept = days || AUDIT_RETENTION_DAYS;
  return kept % 365 === 0
    ? formatMessage({ id: "audit_retention_years" }, { years: kept / 365 })
    : formatMessage({ id: "audit_retention_days" }, { days: kept });
};
